import { Button } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { LoadingButton } from "@mui/lab";
import { useRecordContext, useRefresh } from "react-admin";
import { useState } from "react";
import { reportError } from "../backoffice.utils";
import { serverAPI } from "../api/server.api";
import { Student, studentsProvider } from "../providers/studentsProvider";
import { useMutation } from "react-query";
import { useDialog } from "../hooks/useDialog";
import { SendDunningNoticeDialog, SendDunningNoticeFormValues } from "../dialogs/SendDunningNoticeDialog";
import { DunningProcess, type EmailDraft } from "../generated/backendClient";
import { LocalizedError } from "../utils/LocalizedError";
import { draftDunningNotice, postponeDunningProcess } from "../api/backend.api";
import { DateTime } from "luxon";
import { EventPropagationStopper } from "../misc/EventPropagationStopper";

export const SendSecondDunningNoticeButton = ({
  variant = "rowButton",
}: {
  variant?: "rowButton" | "dialogButton";
}) => {
  const record = useRecordContext<Student | DunningProcess>();
  const refresh = useRefresh();
  const { dialogProps, openDialog, closeDialog } = useDialog();
  const [draftingEmail, setDraftingEmail] = useState(false);
  const [dunningProcessId, setDunningProcessId] = useState<string>();
  const [draft, setDraft] = useState<EmailDraft | undefined>();

  const draftEmailAndOpenDialog = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setDraftingEmail(true);
    try {
      const { dunningProcess, draft } = await draftDunningNotice(record, "second_dunning_notice");
      if (!dunningProcess.first_dunning_notice) {
        showErrorToast("Es wurde noch keine 1. Mahnung verschickt.");
        return;
      }
      const firstDunningNoticeSentAt = DateTime.fromISO(dunningProcess.first_dunning_notice.email_sent_at);
      const dateDiff = Math.floor(DateTime.now().diff(firstDunningNoticeSentAt, "days").days);
      if (dateDiff < 14) {
        showErrorToast("Die 2. Mahnung kann erst 14 Tage nach der 1. Mahnung verschickt werden.");
        return;
      }
      setDunningProcessId(dunningProcess.id);
      setDraft(draft);
      openDialog();
    } catch (error) {
      if (error instanceof LocalizedError) {
        showErrorToast(error.message);
      } else {
        reportError("Failed to draft second dunning notice", error);
        showErrorToast(
          "Oje, es ist ein Fehler beim Vorbereiten der 2. Mahnung aufgetreten. Bitte wende Dich an die Technik.",
        );
      }
    } finally {
      setDraftingEmail(false);
    }
  };

  const { mutateAsync: sendSecondDunningNotice } = useMutation(
    async ({ emailSubject, emailText, postponementDate }: SendDunningNoticeFormValues) => {
      if (!dunningProcessId) {
        throw new Error("dunningProcessId not set");
      }
      let student: Student;
      if (isDunningProcess(record)) {
        student = (await studentsProvider.getOne("students", { id: record.student })).data;
      } else {
        student = record;
      }
      await serverAPI.sendDunningNotice({
        student,
        dunningProcessId,
        noticeType: "second_dunning_notice",
        emailSubject,
        emailText,
      });
      await postponeDunningProcess(dunningProcessId, student.id, postponementDate);
    },
    {
      onSuccess: () => {
        refresh();
        showSuccessToast("2. Mahnung erfolgreich versandt.");
        closeDialog();
      },
      onError: (error) => {
        reportError("Failed to send second dunning notice", error);
        showErrorToast("Fehler beim Versenden der 2. Mahnung");
      },
    },
  );

  return (
    <EventPropagationStopper>
      {variant === "rowButton" ? (
        <LoadingButton
          variant="outlined"
          loading={draftingEmail}
          startIcon={<SendIcon />}
          disabled={!record}
          onClick={draftEmailAndOpenDialog}
        >
          {"2.\u00A0Mahnung"}
        </LoadingButton>
      ) : (
        <Button variant="text" disableRipple sx={{ height: "20px" }} onClick={draftEmailAndOpenDialog}>
          2. Mahnung versenden
        </Button>
      )}
      <SendDunningNoticeDialog
        title="2. Mahnung"
        {...dialogProps}
        initialFormValues={{
          emailSubject: draft?.subject ?? "",
          emailText: draft?.text ?? "",
          postponementDate: DateTime.now().plus({ days: 14 }),
        }}
        onSubmit={sendSecondDunningNotice}
      />
    </EventPropagationStopper>
  );
};

function isDunningProcess(x: Student | DunningProcess): x is DunningProcess {
  if (x && typeof x === "object") {
    const { id, student, invoices } = x as any;
    return typeof id === "string" && typeof student === "string" && Array.isArray(invoices);
  }
  return false;
}
